import { Table } from '@/components/tables/table';
import { TableColumnsType, Switch, Button, Tooltip, App } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import { formatDateToHHmmDDMMYYYY } from '@/utils/common';
import { css, Global } from '@emotion/react';

type BannerHeaderTableProps = {
  data?: BannerHeader[];
  loading?: boolean;
  pagination?: {
    curPage: number;
    limitPage: number;
    totalRows: number;
    totalPage: number;
  };
  currentPage?: number;
  pageSize?: number;
  onEdit?: (record: BannerHeader) => void;
  onDelete?: (record: BannerHeader) => void;
  onToggleDisplay?: (
    record: BannerHeader,
    platform: 'PC' | 'MOBILE' | 'APP',
    checked: boolean
  ) => void;
  onPageChange?: (page: number, pageSize: number) => void;
};

export function BannerHeaderTable({
  data = [],
  loading = false,
  pagination,
  currentPage = 1,
  pageSize = 10,
  onEdit,
  onDelete,
  onToggleDisplay,
  onPageChange
}: BannerHeaderTableProps) {
  const { modal } = App.useApp();

  const handleDelete = (record: BannerHeader) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xóa Banner Header này không?',
      content: (
        <em>
          Khi chọn xóa Banner Header này thì toàn bộ dữ liệu đã tạo sẽ bị xóa.
        </em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => onDelete?.(record)
    });
  };

  const columns: TableColumnsType<BannerHeader> = [
    {
      title: 'Stt',
      render: (_value, _record, index) => {
        return pagination
          ? (pagination.curPage - 1) * pagination.limitPage + index + 1
          : index + 1;
      },
      width: 40
    },
    {
      title: 'Tiêu đề Banner Header',
      dataIndex: 'title',
      key: 'title',
      width: 230
    },
    {
      title: 'Hiển thị cho PC',
      dataIndex: 'showOnPc',
      key: 'showOnPc',
      width: 115,
      render: (_value: any, record: BannerHeader) => {
        const isPCActive = Boolean(record.showOnPc);

        return (
          <Switch
            checked={isPCActive}
            onChange={(checked) => onToggleDisplay?.(record, 'PC', checked)}
          />
        );
      }
    },
    {
      title: 'Hiển thị cho Mobile',
      dataIndex: 'showOnMobile',
      key: 'showOnMobile',
      width: 140,
      render: (_value: any, record: BannerHeader) => {
        const isMobileActive = Boolean(record.showOnMobile);

        return (
          <Switch
            checked={isMobileActive}
            onChange={(checked) => onToggleDisplay?.(record, 'MOBILE', checked)}
          />
        );
      }
    },
    {
      title: 'Hiển thị cho App',
      dataIndex: 'showOnApp',
      key: 'showOnApp',
      width: 125,
      render: (_value: any, record: BannerHeader) => {
        const isAppActive = Boolean(record.showOnApp);

        return (
          <Switch
            checked={isAppActive}
            onChange={(checked) => onToggleDisplay?.(record, 'APP', checked)}
          />
        );
      }
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (_, record) => {
        const createdAt = record.createdAt;
        const createdBy = record.createdName;
        return `${formatDateToHHmmDDMMYYYY(createdAt || '')} ${createdBy ? '[' + createdBy + ']' : ''}`;
      }
    },
    {
      title: 'Cập nhật mới nhất',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (_, record) => {
        const updatedAt = record.updatedAt;
        const updatedBy = record.updatedName;
        return `${formatDateToHHmmDDMMYYYY(updatedAt || '')} ${updatedBy ? '[' + updatedBy + ']' : ''}`;
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          <Tooltip title='Chỉnh sửa'>
            <Button
              type='primary'
              icon={<EditOutlined />}
              size='small'
              onClick={() => onEdit?.(record)}
            />
          </Tooltip>
          <div
            style={{ border: '1px solid #0000000F', height: '1.6rem' }}
          ></div>
          <Tooltip title='Xóa'>
            <Button
              type='primary'
              danger
              icon={<DeleteOutlined />}
              size='small'
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </div>
      )
    }
  ];

  return (
    <>
      <Global
        styles={css`
          .ant-modal-confirm-btns {
            display: flex !important;
            justify-content: center !important;
            padding-top: 2rem;
          }
        `}
      />
      <Table<BannerHeader>
        rowKey={(r) => String(r.id)}
        loading={loading}
        columns={columns}
        dataSource={data}
        enableSticky
        stickyOffsetHeader={60}
        pagination={{
          // showQuickJumper: true,
          showSizeChanger: true,
          current: currentPage,
          pageSize: pageSize,
          total: pagination?.totalRows || 0,
          onChange: onPageChange,
          onShowSizeChange: onPageChange
        }}
      />
    </>
  );
}
